<!-- Copyright: Seán I. O'Donoghue -->
<!-- The gallery's slideshow (Seán, 2026-10-26): its posters and artworks, as filtered, one -->
<!-- after another on black, full screen (the gallery asks for it; on an iPhone, without full -->
<!-- screen, the window). Below, the poster's caption and the controls, hidden while the -->
<!-- mouse rests: back, pause or play, next, the count, close. Keys: ← →, Space, Esc; a swipe -->
<!-- steps. -->
<template>
  <Teleport to="body">
    <div
      ref="stage"
      class="slideshow"
      :class="{isIdle}"
      role="dialog"
      aria-modal="true"
      aria-label="Poster slideshow"
      tabindex="-1"
      @pointermove="wake"
      @pointerdown="onPointerDown"
      @pointerup="onPointerUp">
      <div ref="slideA" class="slide" />
      <div ref="slideB" class="slide" />
      <div class="bar">
        <p v-if="poster" class="caption" aria-live="polite">
          <span class="collection">VIZBI {{ poster.year }} {{ poster.posterNum }}</span>
          <span class="title">{{ poster.title }}</span>
          <span class="authors">{{ poster.authors }}</span>
        </p>
        <div class="controls">
          <button
            v-for="control in controls"
            :key="control.label"
            type="button"
            :aria-label="control.label"
            :title="control.label"
            @click="control.act">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="control.icon" /></svg>
          </button>
          <span class="count">{{ index + 1 }} / {{ posters.length }}</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {FADE_MS, createSlideshow} from "@/composables/posterSlideshow.js";

const props = defineProps({
  posters: {type: Array, required: true}, // the gallery's, as shown (filtered, in its order)
});
const emit = defineEmits(["close"]);

const ICONS = {
  previous: "M15.4 7.4 14 6l-6 6 6 6 1.4-1.4L10.8 12z",
  next: "M8.6 16.6 10 18l6-6-6-6-1.4 1.4 4.6 4.6z",
  pause: "M6 5h4v14H6zm8 0h4v14h-4z",
  play: "M8 5v14l11-7z",
  close: "M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 "
    + "13.4 12z",
};
const IDLE_MS = 2500; // the caption and controls hide after the mouse rests this long
const SWIPE_PX = 50;
const [stage, slideA, slideB] = [ref(), ref(), ref()];
const [index, isPlaying, isIdle] = [ref(0), ref(true), ref(false)];
const poster = computed(() => props.posters[index.value]);
let slideshow = null;

function togglePlaying() {
  isPlaying.value = !isPlaying.value;
  slideshow.setPlaying(isPlaying.value);
}
const controls = computed(() => [
  {label: "Previous", icon: ICONS.previous, act: () => slideshow.step(-1)},
  {label: isPlaying.value ? "Pause" : "Play", icon: ICONS[isPlaying.value ? "pause" : "play"],
    act: togglePlaying},
  {label: "Next", icon: ICONS.next, act: () => slideshow.step(1)},
  {label: "Close slideshow", icon: ICONS.close, act: close},
]);

// leaving: out of full screen too (its own exit, e.g. Esc, closes the slideshow: onFullscreen)
function close() {
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  emit("close");
}
function onFullscreen() {
  if (!document.fullscreenElement) emit("close");
}

let idleTimer = 0;
function wake() {
  isIdle.value = false;
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => (isIdle.value = true), IDLE_MS);
}

const KEYS = {
  ArrowLeft: () => slideshow.step(-1),
  ArrowRight: () => slideshow.step(1),
  " ": togglePlaying,
  Escape: close,
};
function onKey(event) {
  const act = KEYS[event.key];
  const isButtonSpace = event.key === " " && event.target.closest?.("button");
  if (!act || isButtonSpace) return; // Space on a button presses it
  event.preventDefault();
  wake();
  act();
}

// a swipe left or right steps (touch); a tap only wakes the controls
let swipeStart = null;
const onPointerDown = (event) => (swipeStart = event.clientX);
function onPointerUp(event) {
  const distance = event.clientX - (swipeStart ?? event.clientX);
  swipeStart = null;
  wake();
  if (Math.abs(distance) < SWIPE_PX || event.target.closest("button")) return;
  slideshow.step(distance < 0 ? 1 : -1);
}

onMounted(() => {
  slideshow = createSlideshow({
    elements: [slideA.value, slideB.value],
    posters: props.posters,
    onShow: (shown) => (index.value = shown),
  });
  slideshow.goTo(0);
  stage.value.focus();
  wake();
  window.addEventListener("keydown", onKey);
  document.addEventListener("fullscreenchange", onFullscreen);
});
onBeforeUnmount(() => {
  slideshow?.destroy();
  clearTimeout(idleTimer);
  window.removeEventListener("keydown", onKey);
  document.removeEventListener("fullscreenchange", onFullscreen);
});
</script>

<style scoped>
.slideshow {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: black;
  color: #eee;
  text-align: left;
  outline: none;
  --fade: v-bind("`${FADE_MS}ms`");
}
.slideshow.isIdle {
  cursor: none;
}
/* the two viewers, one over the other: the shown one opaque, crossfaded (posterSlideshow.js) */
.slide {
  position: absolute;
  inset: 0 0 4.5rem;
  opacity: 0;
  transition: opacity var(--fade) ease;
}
@media (prefers-reduced-motion: reduce) {
  .slide {
    transition: none;
  }
}
/* the caption and controls along the bottom; hidden while the mouse rests */
.bar {
  position: absolute;
  inset: auto 0 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 4.5rem;
  padding: 0.5rem 1rem;
  box-sizing: border-box;
  transition: opacity 0.3s ease;
}
.isIdle .bar {
  opacity: 0;
}
.caption {
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 0.95rem;
}
.collection {
  margin-right: 0.6em;
  color: var(--brand-color);
  font-weight: 700;
}
.title {
  margin-right: 0.6em;
  color: white;
  font-weight: 700;
}
.authors {
  color: #aaa;
}
.controls {
  flex: none;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}
/* as the poster page's viewer controls (global.css styles every button: reset here) */
.controls button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 1px solid rgb(255 255 255 / 0.25);
  border-radius: 4px;
  box-shadow: none;
  background: rgb(255 255 255 / 0.08);
  color: #eee;
  cursor: pointer;
}
.controls button:hover {
  background: rgb(255 255 255 / 0.18);
  color: white;
}
.controls button:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
.controls svg {
  width: 1.3rem;
  height: 1.3rem;
  fill: currentColor;
}
.count {
  min-width: 4.5rem;
  text-align: right;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  color: #aaa;
}
/* a phone: the caption above the controls */
@media (max-width: 600px) {
  .slide {
    bottom: 7rem;
  }
  .bar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.4rem;
    min-height: 7rem;
  }
  .controls {
    justify-content: center;
  }
}
</style>
